import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { StickyNote, GitBranch, Mail, FileText, CheckSquare, Pencil, Phone, MessageSquare, ChevronDown, ChevronRight, CalendarCheck, BellRing, Search, ExternalLink } from "lucide-react";

const CATS = {
  note: ["Note", StickyNote, "#565E68"],
  stage: ["Stage", GitBranch, "#1C2530"],
  email: ["Email", Mail, "#3E8F99"],
  reply: ["Reply", MessageSquare, "#2F7A3E"],
  call: ["Call", Phone, "#E2711D"],
  proposal: ["Proposal", FileText, "#7A4E9E"],
  approval: ["Approval", CheckSquare, "#B23A2E"],
  field: ["Change", Pencil, "#94A3AD"],
  meeting: ["Meeting", CalendarCheck, "#C99A1E"],
  nudge: ["Nudge", BellRing, "#E2711D"],
};
const FILTERS = [["all", "All"], ["email", "Emails"], ["reply", "Replies"], ["call", "Calls"], ["meeting", "Meetings"], ["proposal", "Proposals"], ["approval", "Approvals"], ["nudge", "Nudges"], ["field", "Changes"], ["note", "Notes"]];
const readPref = (k, d) => { try { return localStorage.getItem(k) || d; } catch { return d; } };

const rel = (ts) => {
  const d = (Date.now() - new Date(ts).getTime()) / 1000;
  if (isNaN(d)) return "";
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

function Item({ e }) {
  const [open, setOpen] = useState(false);
  const [label, Icon, color] = CATS[e.category] || CATS.note;
  const dir = e.meta?.direction;
  return (
    <div className="relative pl-8" data-testid="lifecycle-item" data-category={e.category}>
      <span className="absolute left-0 top-0.5 w-6 h-6 flex items-center justify-center border bg-white" style={{ borderColor: color, color }}><Icon className="w-3 h-3" /></span>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 text-white" style={{ background: color }}>{label}{dir ? ` · ${dir}` : ""}</span>
        <span className="font-mono text-[10px] text-[#1C2530]/50 tabular-nums" title={new Date(e.ts).toLocaleString()}>{rel(e.ts)} · {new Date(e.ts).toLocaleString()}</span>
        {e.actor && <span className="font-mono text-[10px] text-[#1C2530]/40">· {e.actor}</span>}
        {e.meta?.jarvis && <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#6B4EFF] text-[#6B4EFF] bg-[#6B4EFF]/5" data-testid="jarvis-badge">{e.meta.overruled ? "Jarvis · overruled" : "Jarvis"}</span>}
        {e.meta?.approved_by && <span className="font-mono text-[9px] px-1.5 py-0.5 bg-[#EFECE5] text-[#1C2530]/70" data-testid="jarvis-approver-chip">approved by {e.meta.approved_by}{e.meta.mode ? ` · ${e.meta.mode}` : ""}</span>}
        {e.meta?.mail_link && <Link to={e.meta.mail_link} data-testid="lifecycle-open-mail" className="font-mono text-[10px] uppercase text-[#3E8F99] hover:underline flex items-center gap-0.5"><ExternalLink className="w-3 h-3" /> Open in Mail</Link>}
      </div>
      <div className="text-sm text-[#1C2530] mt-0.5 flex items-start gap-1">
        {e.detail && <button onClick={() => setOpen((o) => !o)} className="mt-0.5 text-[#1C2530]/40 hover:text-[#1C2530]" data-testid="lifecycle-expand">{open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</button>}
        <span>{e.title}</span>
      </div>
      {open && e.detail && <div className="text-xs text-[#1C2530]/70 mt-1 ml-5 whitespace-pre-wrap border-l-2 border-[#DFDAD0] pl-2" data-testid="lifecycle-detail">{e.detail}</div>}
    </div>
  );
}

export default function LifecycleTimeline({ leadId, refreshKey, header }) {
  const [data, setData] = useState(null);
  const [filter, setFilterRaw] = useState(() => readPref("vis-lifecycle-filter", "all"));
  const [recent, setRecentRaw] = useState(() => readPref("vis-lifecycle-recent", "0") === "1");
  const [q, setQ] = useState("");
  const setFilter = (k) => { setFilterRaw(k); try { localStorage.setItem("vis-lifecycle-filter", k); } catch {} };
  const setRecent = (v) => { setRecentRaw(v); try { localStorage.setItem("vis-lifecycle-recent", v ? "1" : "0"); } catch {} };
  useEffect(() => { api.get(`/leads/${leadId}/lifecycle`).then((r) => setData(r.data)).catch(() => setData({ events: [], counts: {}, total: 0 })); }, [leadId, refreshKey]);
  const cutoff = Date.now() - 30 * 86400000;
  const ql = q.trim().toLowerCase();
  const events = (data?.events || []).filter((e) => (filter === "all" || e.category === filter) && (!recent || new Date(e.ts).getTime() >= cutoff)
    && (!ql || `${e.title || ""} ${e.detail || ""} ${e.actor || ""}`.toLowerCase().includes(ql)));
  return (
    <div data-testid="lifecycle-timeline">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Lead lifecycle · {data ? `${data.total} events` : "loading…"}</div>
        {header}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-[#1C2530]/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search timeline…" data-testid="lifecycle-search"
            className="w-full border border-[#DFDAD0] bg-white pl-7 pr-2 py-1 text-sm outline-none focus:border-[#1C2530]" />
        </div>
        <button onClick={() => setRecent(!recent)} aria-pressed={recent} data-testid="lifecycle-recent-toggle"
          className={`font-mono text-[10px] uppercase tracking-wider px-2 py-1 border ${recent ? "bg-[#1C2530] text-[#F5F3EE] border-[#1C2530]" : "border-[#DFDAD0] text-[#1C2530]/60 hover:bg-[#EFECE5]"}`}>Last 30 days</button>
        {(ql || recent || filter !== "all") && <span className="font-mono text-[10px] text-[#1C2530]/50" data-testid="lifecycle-shown">{events.length} shown</span>}
      </div>
      <div className="flex flex-wrap gap-1 mb-4" data-testid="lifecycle-filters">
        {FILTERS.map(([k, l]) => {
          const n = k === "all" ? data?.total : data?.counts?.[k];
          if (k !== "all" && !n && filter !== k) return null;
          return (
            <button key={k} onClick={() => setFilter(k)} data-testid={`lifecycle-filter-${k}`}
              className={`font-mono text-[10px] uppercase tracking-wider px-2 py-1 border transition-colors ${filter === k ? "bg-[#1C2530] text-[#F5F3EE] border-[#1C2530]" : "border-[#DFDAD0] text-[#1C2530]/60 hover:bg-[#EFECE5]"}`}>
              {l}{n ? ` ${n}` : ""}
            </button>
          );
        })}
      </div>
      <div className="relative space-y-4 before:absolute before:left-3 before:top-2 before:bottom-2 before:w-px before:bg-[#DFDAD0]">
        {data && events.length === 0 && <div className="font-mono text-xs text-[#1C2530]/40 pl-8" data-testid="lifecycle-empty">NO_EVENTS</div>}
        {events.map((e, i) => <Item key={`${e.ts}-${i}`} e={e} />)}
      </div>
    </div>
  );
}
